/**
 * InstallPrompt Component
 *
 * Shows a banner prompting users to install the PWA.
 * - Android Chrome: Catches beforeinstallprompt and triggers native install
 * - iOS Safari: Shows instructions since iOS doesn't support beforeinstallprompt
 * - Dismissal stored in localStorage for 30 days
 */

import React, { useState, useEffect, useCallback } from 'react';

// How long to hide the prompt after dismissal (30 days)
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'pwa-install-dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type BrowserType = 'ios-safari' | 'chrome' | 'firefox' | 'other-mobile' | 'desktop';

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [browserType, setBrowserType] = useState<BrowserType>('desktop');
  const [isStandalone, setIsStandalone] = useState(false);

  // Check if already installed or recently dismissed
  useEffect(() => {
    // Check if running as standalone (already installed)
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as unknown as { standalone?: boolean }).standalone === true;
    setIsStandalone(standalone);

    if (standalone) return;

    // Check if recently dismissed
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissedTime < DISMISS_DURATION_MS) {
        return;
      }
      // Dismissal expired, remove it
      localStorage.removeItem(STORAGE_KEY);
    }

    // Detect browser type
    const ua = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/Firefox/.test(ua);
    const isFirefox = /Firefox/.test(ua);
    const isChrome = /Chrome/.test(ua) && !/Edge/.test(ua);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    if (isIOSDevice && isSafari) {
      setBrowserType('ios-safari');
      setShowPrompt(true);
      return;
    }

    if (isMobile && isFirefox) {
      setBrowserType('firefox');
      setShowPrompt(true);
      return;
    }

    // For Chrome/Edge, show prompt immediately with instructions
    // Will upgrade to native install button if beforeinstallprompt fires
    if (isMobile && isChrome) {
      setBrowserType('chrome');
      setShowPrompt(true);
    } else if (isMobile) {
      setBrowserType('other-mobile');
      setShowPrompt(true);
      return;
    }

    // Listen for beforeinstallprompt (Android Chrome, Edge, etc.)
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
    } catch {
      // User cancelled or error occurred
    }

    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setShowPrompt(false);
  }, []);

  // Don't show if already installed or not applicable
  if (isStandalone || !showPrompt) {
    return null;
  }

  const getInstructions = () => {
    const subtitleClass = "text-xs text-[var(--color-text-muted)] flex items-center gap-1";
    switch (browserType) {
      case 'ios-safari':
        return (
          <span className={subtitleClass}>
            Tap <ShareIcon /> then "Add to Home Screen"
          </span>
        );
      case 'firefox':
        return (
          <span className={subtitleClass}>
            Tap <MenuIcon /> then "Add app to Home screen"
          </span>
        );
      case 'chrome':
        return (
          <span className={subtitleClass}>
            Tap <MenuIcon /> then "Add to Home screen"
          </span>
        );
      case 'other-mobile':
        return (
          <span className={subtitleClass}>
            Use browser menu to add to home screen
          </span>
        );
      default:
        return (
          <span className={subtitleClass}>Add to your home screen for quick access</span>
        );
    }
  };

  const showInstallButton = browserType === 'chrome' && deferredPrompt;
  const showInstructions = browserType !== 'chrome' || !deferredPrompt;

  return (
    <div className="fixed bottom-[calc(var(--bottom-nav-height)+env(safe-area-inset-bottom))] left-0 right-0 z-[999] md:hidden flex items-center justify-between gap-3 p-3 px-4 bg-bg-alt border-t border-border shadow-[0_-4px_12px_var(--overlay-black-10)] dark:shadow-[0_-4px_12px_var(--overlay-black-30)] animate-slideUp">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-primary rounded-[10px] text-white">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </div>
        <div className="flex flex-col gap-[2px] min-w-0">
          <span className="text-[0.9rem] font-semibold text-[var(--color-text)]">Install Luminaria</span>
          {showInstructions ? getInstructions() : (
            <span className="text-xs text-[var(--color-text-muted)] flex items-center gap-1">
              Add to your home screen for quick access
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        {showInstallButton && (
          <button
            className="px-4 py-2 rounded-lg text-[0.85rem] font-medium cursor-pointer transition-opacity duration-150 border-none bg-primary text-white hover:opacity-90 active:opacity-80"
            onClick={handleInstall}
          >
            Install
          </button>
        )}
        <button
          className="px-3 py-2 rounded-lg text-[0.85rem] font-medium cursor-pointer transition-all duration-150 border-none bg-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-surface"
          onClick={handleDismiss}
        >
          {showInstallButton ? 'Not now' : 'Got it'}
        </button>
      </div>
    </div>
  );
}

// iOS Share icon component
function ShareIcon() {
  return (
    <svg
      className="inline-block align-middle text-primary"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

// Firefox/Android menu icon (three dots)
function MenuIcon() {
  return (
    <svg
      className="inline-block align-middle text-primary"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}

export default InstallPrompt;
